import { ReactFlowProvider } from '@xyflow/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LoaderIcon } from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import BlocksSidebar from './BlocksSidebar/BlocksSidebar';
import Canvas from './Canvas/Canvas';
import CodeEditor from './CodeEditor/CodeEditor';
import { DnDProvider } from './DnDContext';
import HardwareSettingsModal from './HardwareSettingsModal/HardwareSettingsModal';
import Header from './Header/Header';
import { AllFlowNodes } from './Nodes/types';
import SettingsSidebar from './SettingsSidebar/SettingsSidebar';
import { useHandleSave } from './customHooks/useHandleSaveLogic';
import useHandleSaveScript from './customHooks/useHandleSaveScript';
import { useLogicInitialization } from './customHooks/useLogicInitialization';
import { useScriptInitialization } from './customHooks/useScriptInitialization';
import { useSensorAvailability } from './customHooks/useSensorAvailability';
import { useLogicValidation } from './customHooks/useValidationLogic';
import { StyledContent, StyledPageContainer } from './styles';
import { updateNodeData } from './util/updateNodeData';
import EnableLogicEditorModal from './customHooks/EnableLogicEditorModal';

function LogicEditor() {
  const { id: unitId } = useParams();

  // Initialize logic editor data for visual editor
  const {
    nodes,
    setNodes,
    onNodesChange,
    edges,
    setEdges,
    onEdgesChange,
    settings,
    setSettings,
    selectedSensors,
    setSelectedSensors,
    unit,
    refetchUnit,
    isRefetchingUnit,
    isLoadingUnits,
    isSuccess,
    isError,
    isDirty,
    setIsDirty,
    initialStateRef,
  } = useLogicInitialization(unitId!);

  // Initialize script data for code editor
  const {
    script,
    refetchScript,
    code,
    setCode,
    isLoadingScript,
    isRefetchingScript,
    isEditingCode,
    setIsEditingCode,
  } = useScriptInitialization({ unit, unitId: unitId!, setIsDirty });

  // Code editor states and functions
  const [isViewingCode, setIsViewingCode] = useState(isEditingCode);

  useEffect(() => {
    if (isEditingCode) {
      setIsViewingCode(true);
    }
  }, [isEditingCode]);
  useEffect(() => {
    localStorage.setItem(
      'logicEditor:viewCodeEditor',
      JSON.stringify(isViewingCode),
    );
  }, [isViewingCode]);

  // Validation logic
  const { validateNodeOptions } = useLogicValidation();

  // General UI state
  const [isHardwareModalOpen, setIsHardwareModalOpen] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [invalidNodeId, setInvalidNodeId] = useState<string | null>(null);

  // find the selected node based on selectedNodeId
  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) ?? null,
    [nodes, selectedNodeId],
  );

  // check if there is a language switch node in the current nodes
  const hasLanguageSwitchNode = useMemo(
    () => nodes.some((node) => node.type === 'action-switchLanguage'),
    [nodes],
  );

  // Node availability handler used for disabling nodes in blockssidebar
  const { sensorNodeAvailability } = useSensorAvailability(
    nodes,
    settings,
    selectedSensors,
    unit?.nfcTagIdList ?? [],
  );

  // Sync language selective setting with presence of language switch node
  useEffect(() => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      languageSelective: hasLanguageSwitchNode,
    }));
  }, [hasLanguageSwitchNode, setSettings]);

  // Save logic handler
  const { handleSave, isLoading: isSaving } = useHandleSave({
    unitId: unitId!,
    nodes,
    edges,
    settings,
    setNodes,
    setInvalidNodeId,
    setIsDirty,
    selectedSensors,
    initialStateRef,
  });

  // Save script handler
  const { handleSaveScript, isLoading: savingScript } = useHandleSaveScript({
    unitId: unitId!,
    code,
    setIsDirty,
  });

  // Node data updater with validation
  const updateNodeDataExecutor = useCallback(
    (nodeId: string, newData: Partial<AllFlowNodes['data']>) => {
      setNodes((currentNodes) => {
        return updateNodeData(
          nodeId,
          newData,
          currentNodes,
          edges,
          validateNodeOptions,
        );
      });
    },
    [setNodes, validateNodeOptions, edges],
  );

  // Loading state
  const isLoading =
    isLoadingUnits ||
    (!isSuccess && !isError) ||
    isLoadingScript ||
    (isViewingCode && isRefetchingScript) ||
    (isViewingCode && isRefetchingUnit);

  // this is placeholder until we have skeleton loaders
  if (isLoading) {
    return (
      <LoaderIcon
        style={{
          width: '50px',
          height: '50px',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      />
    );
  }

  return (
    <ReactFlowProvider>
      <DnDProvider>
        <StyledPageContainer>
          <EnableLogicEditorModal
            isWarningModalOpen={isWarningModalOpen}
            setIsWarningModalOpen={setIsWarningModalOpen}
            setIsEditingCode={setIsEditingCode}
            refetchScript={refetchScript}
            refetchUnit={refetchUnit}
            isLoading={isLoading}
            isSaving={isSaving}
            savingScript={savingScript}
            handleSave={handleSave}
          />
          <HardwareSettingsModal
            settings={settings}
            setSettings={setSettings}
            open={isHardwareModalOpen}
            setIsHardwareModalOpen={setIsHardwareModalOpen}
          />

          <Header
            handleSave={async () => {
              // if we were in devmode but are not anymore, show warning modal that code will be overwritten
              if (!isEditingCode && !unit?.logicEditorEnabled) {
                setIsWarningModalOpen(true);
                return;
              }
              // else just save normally and refetch the script to show the updated code in codeview
              await handleSave();
              await refetchScript();
              // also refetch the unit incase logicEditorEnabled changed
              await refetchUnit();
            }}
            enableSave={isDirty}
            isSaving={isSaving}
            setIsHardwareModalOpen={setIsHardwareModalOpen}
            isViewingCode={isViewingCode || isEditingCode}
            toggleCodeView={() => {
              setIsViewingCode(!isViewingCode);
              // reset selected nodeid to force reactflow to recalculate the selected node and avoud trying to select a node that is not there
              setSelectedNodeId(null);
            }}
            isEditingCode={isEditingCode}
            setIsEditingCode={setIsEditingCode}
            saveScript={handleSaveScript}
            savingScript={savingScript}
            refetchScript={refetchScript}
            refetchUnit={refetchUnit}
          />

          <StyledContent>
            {!isViewingCode ? (
              <>
                <BlocksSidebar
                  sensorNodeAvailability={sensorNodeAvailability}
                  isSaving={isSaving}
                />

                <Canvas
                  invalidNodeId={invalidNodeId}
                  setInvalidNodeId={setInvalidNodeId}
                  setSelectedNodeId={setSelectedNodeId}
                  nodes={nodes}
                  edges={edges}
                  setNodes={setNodes}
                  setEdges={setEdges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  selectedSensors={selectedSensors}
                  setSelectedSensors={setSelectedSensors}
                  settings={settings}
                  isSaving={isSaving}
                />
              </>
            ) : (
              <CodeEditor
                setCode={setCode}
                script={script}
                isEditingCode={isEditingCode}
              />
            )}

            {!isEditingCode && (
              <SettingsSidebar
                selectedNode={selectedNode as AllFlowNodes}
                settings={settings}
                setSettings={setSettings}
                updateNodeData={updateNodeDataExecutor}
                magicModels={unit?.nfcTagIdList ?? []}
                selectedSensors={selectedSensors}
                setSelectedSensors={setSelectedSensors}
                isSaving={isSaving}
                isViewingCode={isViewingCode}
              />
            )}
          </StyledContent>
        </StyledPageContainer>
      </DnDProvider>
    </ReactFlowProvider>
  );
}

export default LogicEditor;
